import { useIntl } from '@capsicum/common';
import { Accordion, DocumentPickerUpload } from '@capsicum/common-web';
import {
	type CategoryStepProps,
	type CategoryStepsRefProps,
	SOI_MAX_FILE_SIZE,
	type SOIParsedGroup,
	useSOICategoryStep
} from '@capsicum/features/regulations/shared';
import { type ForwardedRef, forwardRef, useCallback, useImperativeHandle, useRef } from 'react';
import styled from 'styled-components';

export const CategoryStep = forwardRef(
	({ category, stepCounter }: CategoryStepProps, ref: ForwardedRef<CategoryStepsRefProps>) => {
		const { $t } = useIntl();
		const { getGroupDescription, groups, hasUniqueType, onUploadFiles } = useSOICategoryStep({
			category
		});

		const resetHandlers = useRef<Record<string, () => void>>({});

		const registerResetHandler = (requirementId: string, resetHandler: { removeAllFiles: () => void } | null) => {
			if (resetHandler) {
				resetHandlers.current[requirementId] = resetHandler.removeAllFiles;
			} else {
				delete resetHandlers.current[requirementId];
			}
		};

		const resetCategoryFiles = () => {
			Object.values(resetHandlers.current).forEach(resetHandler => resetHandler());
		};

		useImperativeHandle(ref, () => ({
			resetCategoryFiles
		}));

		const getAccordionItemsFromGroup = useCallback(
			(group: Array<SOIParsedGroup>) => {
				return group.map(requirement => ({
					content: (
						<S.Content>
							<S.Subtitle>{requirement.description}</S.Subtitle>
							<S.DocumentsWarning>
								{$t('sourceOfIncome.category.numberOfDocuments', {
									numberOfDocuments: requirement.numberOfDocuments
								})}
							</S.DocumentsWarning>
							<S.UploaderWrapper>
								<DocumentPickerUpload
									id={requirement.requirementId}
									isDisabled={false}
									ref={ref => registerResetHandler(requirement.requirementId, ref)}
									uploadProps={{
										maxSize: SOI_MAX_FILE_SIZE,
										selectionLimit: requirement.numberOfDocuments
									}}
									onUploadedFiles={files =>
										onUploadFiles({ files, requirementId: requirement.requirementId })
									}
								/>
							</S.UploaderWrapper>
						</S.Content>
					),
					id: requirement.requirementId,
					isExpanded: hasUniqueType,
					title: <S.AcordionTitle>{requirement.name}</S.AcordionTitle>
				}));
			},
			[onUploadFiles]
		);

		return (
			<>
				<S.Counter>{stepCounter}</S.Counter>
				<S.Title>{category.name}</S.Title>
				{Object.entries(groups).map(([key, group], index) => (
					<S.Group key={key}>
						<S.Subtitle>{getGroupDescription(index)}</S.Subtitle>
						<S.AccordeonWrapper>
							<Accordion items={getAccordionItemsFromGroup(group)} />
						</S.AccordeonWrapper>
					</S.Group>
				))}
			</>
		);
	}
);

CategoryStep.displayName = 'CategoryStep';

namespace S {
	export const Counter = styled.span`
		display: block;
		color: ${({ theme }) => theme.colors.text.main.subtle};
		${({ theme }) => theme.fonts.headings.title6};
		margin-bottom: ${({ theme }) => theme.spacings.small};
	`;

	export const Title = styled.span`
		color: ${({ theme }) => theme.colors.text.main.enabled};
		${({ theme }) => theme.fonts.headings.title5};
		margin-bottom: ${({ theme }) => theme.spacings.xxsmall};
	`;

	export const Group = styled.div`
		margin-top: ${({ theme }) => theme.spacings.xxsmall};
		margin-bottom: ${({ theme }) => theme.spacings.xsmall};
	`;

	export const Subtitle = styled.span`
		color: ${({ theme }) => theme.colors.text.main.enabled};
		display: block;
		margin-bottom: ${({ theme }) => theme.spacings.xxsmall};
		${({ theme }) => theme.fonts.text.text3};
	`;

	export const DocumentsWarning = styled.span`
		color: ${({ theme }) => theme.colors.text.main.subtle};
		${({ theme }) => theme.fonts.text.caption2};
	`;

	export const AccordeonWrapper = styled.div`
		${({ theme }) => theme.border.radius.medium};
		border: ${({ theme }) => theme.border.width.medium} solid ${({ theme }) => theme.colors.borders.main.divider};
		margin-top: ${({ theme }) => theme.spacings.medium};
	`;

	export const AcordionTitle = styled.span`
		color: ${({ theme }) => theme.colors.text.main.enabled};
		${({ theme }) => theme.fonts.text.text1};
		margin-bottom: ${({ theme }) => theme.spacings.small};
	`;

	export const Content = styled.div`
		padding-top: ${({ theme }) => theme.spacings.medium};
	`;

	export const UploaderWrapper = styled.div`
		margin-top: ${({ theme }) => theme.spacings.medium};
	`;
}
